// Consent Management Platform (CMP) service
// Dynamically loads the appropriate CMP based on user location
import { LocationInfo, getCMPSettingsId } from "./location";

export interface ConsentPreferences {
  analytics: boolean;
  marketing: boolean;
  necessary: boolean;
  preferences: boolean;
}

export interface CMPStatus {
  isLoaded: boolean;
  isReady: boolean;
  hasConsent: boolean;
  preferences: ConsentPreferences | null;
}

class CMPService {
  private static instance: CMPService;
  private status: CMPStatus = {
    isLoaded: false,
    isReady: false,
    hasConsent: false,
    preferences: null,
  };
  private listeners: Array<(status: CMPStatus) => void> = [];
  private settingsId: string | null = null;

  private constructor() {}

  static getInstance(): CMPService {
    if (!CMPService.instance) {
      CMPService.instance = new CMPService();
    }
    return CMPService.instance;
  }

  /**
   * Initialize CMP based on user location
   */
  async initialize(locationInfo: LocationInfo): Promise<void> {
    this.settingsId = getCMPSettingsId(locationInfo.regulation);

    if (!this.settingsId) {
      console.log("No CMP required for this location");
      this.status.isLoaded = true;
      this.status.isReady = true;
      this.status.hasConsent = true;
      this.notifyListeners();
      return;
    }

    try {
      await this.loadCMPScript();
      await this.waitForCMPReady();
      this.setupConsentListeners();
    } catch (error) {
      console.error("Failed to initialize CMP:", error);
      // Fallback: assume consent if CMP fails to load
      this.status.isLoaded = true;
      this.status.isReady = true;
      this.status.hasConsent = true;
      this.notifyListeners();
    }
  }

  /**
   * Load the CMP script
   */
  private async loadCMPScript(): Promise<void> {
    if (this.status.isLoaded || !this.settingsId) {
      return;
    }

    return new Promise((resolve, reject) => {
      // Check if script is already loaded
      const existingScript = document.getElementById("usercentrics-cmp");
      if (existingScript) {
        console.log("CMP script already loaded");
        this.status.isLoaded = true;
        resolve();
        return;
      }

      const script = document.createElement("script");
      script.id = "usercentrics-cmp";
      script.src = "https://app.usercentrics.eu/browser-ui/latest/loader.js";
      script.setAttribute("data-settings-id", this.settingsId!);

      // Add TCF attribute for EU/EEA
      if (this.settingsId === "rWiDt-jTf7k5L2") {
        script.setAttribute("data-tcf-enabled", "");
      }

      script.async = true;

      script.onload = () => {
        console.log("CMP script loaded successfully");
        this.status.isLoaded = true;
        resolve();
      };

      script.onerror = () => {
        console.error("Failed to load CMP script");
        reject(new Error("Failed to load CMP script"));
      };

      document.head.appendChild(script);
    });
  }

  /**
   * Wait for CMP to be ready
   */
  private async waitForCMPReady(): Promise<void> {
    return new Promise((resolve) => {
      const checkReady = () => {
        if (window.UC_UI && window.UC_UI.isInitialized()) {
          this.status.isReady = true;
          resolve();
        } else {
          setTimeout(checkReady, 100);
        }
      };
      checkReady();
    });
  }

  /**
   * Setup consent change listeners
   */
  private setupConsentListeners(): void {
    if (!window.UC_UI) {
      return;
    }

    // Listen for consent changes
    if (typeof window.UC_UI.on === "function") {
      window.UC_UI.on("consentChanged", () => {
        this.updateConsentStatus();
      });
    } else {
      window.addEventListener("ucEvent", (event: Event) => {
        const ev = event as CustomEvent<unknown>;
        const type = ev?.detail?.event || ev?.detail?.type;
        if (type === "consent_status" || type === "consentChanged") {
          this.updateConsentStatus();
        }
      });
    }

    // Get initial consent status
    this.updateConsentStatus();
  }

  /**
   * Update consent status using Usercentrics built-in methods
   */
  private updateConsentStatus(): void {
    if (!window.UC_UI || !window.UC_UI.isInitialized()) {
      return;
    }

    try {
      // Use Usercentrics built-in methods to check consent
      // You would need to replace these with your actual service IDs
      const analyticsServices = [
        import.meta.env.VITE_POSTHOG_SERVICE_ID,
        import.meta.env.VITE_GOOGLE_ANALYTICS_SERVICE_ID,
        import.meta.env.VITE_GOOGLE_TAG_MANAGER_SERVICE_ID,
      ].filter(Boolean);

      const marketingServices = [import.meta.env.VITE_HUBSPOT_SERVICE_ID].filter(Boolean);

      const analytics = analyticsServices.some((serviceId) =>
        serviceId ? window.UC_UI!.isServiceAllowed(serviceId) : false,
      );

      const marketing = marketingServices.some((serviceId) =>
        serviceId ? window.UC_UI!.isServiceAllowed(serviceId) : false,
      );

      this.status.hasConsent = analytics || marketing;
      this.status.preferences = {
        analytics,
        marketing,
        necessary: true, // Always true
        preferences: false, // Add if you have preference services
      };

      this.notifyListeners();
    } catch (error) {
      console.error("Failed to update consent status:", error);
    }
  }

  /**
   * Subscribe to CMP status changes
   */
  subscribe(listener: (status: CMPStatus) => void): () => void {
    this.listeners.push(listener);

    // Return unsubscribe function
    return () => {
      const index = this.listeners.indexOf(listener);
      if (index > -1) {
        this.listeners.splice(index, 1);
      }
    };
  }

  /**
   * Notify all listeners of status changes
   */
  private notifyListeners(): void {
    this.listeners.forEach((listener) => listener({ ...this.status }));
  }

  /**
   * Get current CMP status
   */
  getStatus(): CMPStatus {
    return { ...this.status };
  }

  /**
   * Check if analytics consent is given
   */
  hasAnalyticsConsent(): boolean {
    return this.status.hasConsent && this.status.preferences?.analytics === true;
  }

  /**
   * Check if marketing consent is given
   */
  hasMarketingConsent(): boolean {
    return this.status.hasConsent && this.status.preferences?.marketing === true;
  }

  /**
   * Show CMP banner
   */
  showBanner(): void {
    if (window.UC_UI && this.status.isReady) {
      window.UC_UI.showSecondLayer();
    }
  }

  /**
   * Hide CMP banner
   */
  hideBanner(): void {
    if (window.UC_UI && this.status.isReady) {
      window.UC_UI.hideSecondLayer();
    }
  }
}

// Global type declarations for Usercentrics
declare global {
  interface Window {
    UC_UI?: {
      isInitialized(): boolean;
      getConsent(): unknown;
      isServiceAllowed(serviceId: string): boolean;
      on(event: string, callback: (data: unknown) => void): void;
      showSecondLayer(): void;
      hideSecondLayer(): void;
    };
  }
}

export const cmpService = CMPService.getInstance();
